/* eslint-disable quotes */
/* eslint-disable indent */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Space,
  Input,
  message,
  Tooltip,
  Spin,
  DatePicker,
  Card,
  Row,
  Col,
  Tag,
  Typography,
  Divider,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  FileCSVOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  UserOutlined,
} from "@ant-design/icons";
import API from "../../../api";
import urls from "../../../api/urls";
import TableContainer from "../../../components/TableContainer";
import Smcs_Loader from "../../../assests/images/loader-new.gif";
import { dateFormat_dmy } from "../../../utils";
import moment from "moment";
import "./style.scss";
import Heading from "../../../components/Heading";

/**
 * Generates a human-readable title from camelCase or snake_case strings
 * @param {string} key - The field key
 * @returns {string} - Formatted title
 */
const generateTitle = (key) => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase())
    .trim();
};

/**
 * Determines if a value is a valid date string
 * @param {any} value - Value to check
 * @returns {boolean} - Whether the value is a valid date
 */
const isValidDateString = (value) => {
  if (!value || typeof value !== "string") return false;
  const date = moment(value);
  return date.isValid() && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
};

/**
 * Determines if a value is a URL
 * @param {any} value - Value to check
 * @returns {boolean} - Whether the value is a URL
 */
const isValidUrl = (value) => {
  if (!value || typeof value !== "string") return false;
  const urlPattern = /^(https?:\/\/)|(www\.)|([a-zA-Z0-9-]+\.[a-zA-Z]{2,})/;
  return (
    urlPattern.test(value) ||
    value.startsWith("http") ||
    value.startsWith("www")
  );
};

/**
 * Gets appropriate column width based on field characteristics
 * @param {string} key - Field key
 * @param {any} sampleValue - Sample value from the field
 * @returns {number} - Column width in pixels
 */
const getColumnWidth = (key, sampleValue) => {
  const keyLower = key.toLowerCase();

  // Serial number columns
  if (keyLower.includes("slno") || keyLower.includes("serno")) return 80;

  // ID fields
  if (keyLower.includes("id") && !keyLower.includes("mobile")) return 120;

  // Date fields
  if (
    keyLower.includes("date") ||
    keyLower.includes("at") ||
    isValidDateString(sampleValue)
  )
    return 160;

  // Mobile/Phone fields
  if (keyLower.includes("mobile") || keyLower.includes("phone")) return 140;

  // Email fields
  if (keyLower.includes("email")) return 200;

  // Address fields - specific widths for different address components
  if (keyLower.includes("address")) return 250;
  if (keyLower.includes("street")) return 300;
  if (keyLower.includes("city")) return 150;
  if (keyLower.includes("state")) return 120;
  if (keyLower.includes("pincode")) return 100;

  // Status fields
  if (keyLower.includes("status")) return 120;

  // Count/Number fields - Increased width for better visibility
  if (keyLower.includes("count") || typeof sampleValue === "number")
    return keyLower?.length * 10 || 100;

  // Array fields (like AWB numbers) - Give more space for AWBs
  if (Array.isArray(sampleValue)) {
    // Special wider column for AWB numbers to accommodate horizontal wrapping
    if (keyLower.includes("awb") || keyLower.includes("number")) return 400;
    return 200;
  }

  // Object fields (nested data)
  if (typeof sampleValue === "object" && sampleValue !== null) return 220;

  // URL fields
  if (
    keyLower.includes("url") ||
    keyLower.includes("link") ||
    isValidUrl(sampleValue)
  )
    return 200;

  // Long text fields
  if (typeof sampleValue === "string" && sampleValue.length > 50) return 200;

  // Default width
  return 150;
};

/**
 * Creates appropriate render function for different field types
 * @param {string} key - Field key
 * @param {any} sampleValue - Sample value to determine type
 * @returns {Function|null} - Render function or null
 */
const getCustomRender = (key, sampleValue) => {
  const keyLower = key.toLowerCase();

  // Date fields
  if (
    keyLower.includes("date") ||
    keyLower.includes("at") ||
    isValidDateString(sampleValue)
  ) {
    const DateRenderer = (text) => {
      if (!text || !isValidDateString(text)) return "-";
      return moment(text).format("DD-MM-YYYY hh:mm A");
    };
    DateRenderer.displayName = `DateRenderer_${key}`;
    return DateRenderer;
  }

  // URL fields
  if (
    keyLower.includes("url") ||
    keyLower.includes("link") ||
    isValidUrl(sampleValue)
  ) {
    const UrlRenderer = (text) => {
      if (!text || !isValidUrl(text)) return "-";
      const url = text.startsWith("http") ? text : `https://${text}`;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1890ff" }}
        >
          {text}
        </a>
      );
    };
    UrlRenderer.displayName = `UrlRenderer_${key}`;
    return UrlRenderer;
  }

  // Number/Count fields with color coding
  if (keyLower.includes("count") && typeof sampleValue === "number") {
    const CountRenderer = (count) => {
      if (typeof count !== "number") return "-";
      const color =
        count > 10 ? "red" : count > 5 ? "orange" : count > 2 ? "gold" : "blue";
      return <Tag color={color}>{count}</Tag>;
    };
    CountRenderer.displayName = `CountRenderer_${key}`;
    return CountRenderer;
  }

  // Array fields (like AWB numbers)
  if (Array.isArray(sampleValue)) {
    const ArrayRenderer = (arrayData) => {
      if (!Array.isArray(arrayData) || arrayData.length === 0) return "-";

      // Custom renderer for AWB numbers based on the image
      if (keyLower.includes("awb")) {
        return (
          <div className="awb-list-container">
            {arrayData.map((awb, index) => (
              <div key={index} className="awb-list-item">
                {awb}
              </div>
            ))}
          </div>
        );
      }

      // Fallback for other arrays - expandable view
      if (arrayData.length > 5) {
        const ExpandableArrayRenderer = () => {
          const [expanded, setExpanded] = React.useState(false);

          return (
            <div className="array-renderer-container">
              <div className="array-total-container">
                <Tag color="blue" className="array-total-tag">
                  Total: {arrayData.length}
                </Tag>
              </div>

              {/* Show first 3 items */}
              <div className="array-preview-container">
                {arrayData.slice(0, 3).map((item, index) => (
                  <Tag key={index} className="array-item-tag">
                    {typeof item === "object"
                      ? JSON.stringify(item)
                      : String(item)}
                  </Tag>
                ))}
              </div>

              {/* Expandable section */}
              {expanded && (
                <div className="array-expanded-container">
                  {arrayData.slice(3).map((item, index) => (
                    <Tag key={index + 3} className="array-expanded-tag">
                      {typeof item === "object"
                        ? JSON.stringify(item)
                        : String(item)}
                    </Tag>
                  ))}
                </div>
              )}

              {/* Toggle button */}
              <Button
                type="link"
                size="small"
                onClick={() => setExpanded(!expanded)}
                className="array-toggle-button"
              >
                {expanded ? "Show Less" : `Show ${arrayData.length - 3} More`}
              </Button>
            </div>
          );
        };
        ExpandableArrayRenderer.displayName = `ExpandableArrayRenderer_${key}`;
        return <ExpandableArrayRenderer />;
      }

      // For smaller lists (≤5 items), show horizontal layout
      return (
        <div className="array-small-container">
          {arrayData.map((item, index) => (
            <Tag key={index} className="array-item-tag">
              {typeof item === "object" ? JSON.stringify(item) : String(item)}
            </Tag>
          ))}
        </div>
      );
    };
    ArrayRenderer.displayName = `ArrayRenderer_${key}`;
    return ArrayRenderer;
  }

  // Status fields with formatting
  if (keyLower.includes("status")) {
    const StatusRenderer = (status) => {
      if (!status) return "-";
      const formattedStatus = String(status)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      const color = status.toLowerCase().includes("approved")
        ? "green"
        : status.toLowerCase().includes("pending")
        ? "orange"
        : "default";
      return <Tag color={color}>{formattedStatus}</Tag>;
    };
    StatusRenderer.displayName = `StatusRenderer_${key}`;
    return StatusRenderer;
  }

  // ID fields with highlighting (but not for objects, as they are handled by grouped columns)
  // Exclude slNo and drsNo from special styling
  if (
    (keyLower.includes("id") || keyLower.includes("no")) &&
    !keyLower.includes("slno") &&
    !keyLower.includes("drsno")
  ) {
    const IdRenderer = (text) => {
      if (!text) return "-";
      return (
        <Text strong style={{ color: "#1890ff" }}>
          {text}
        </Text>
      );
    };
    IdRenderer.displayName = `IdRenderer_${key}`;
    return IdRenderer;
  }

  // Default text renderer for any field that doesn't have a specific renderer
  const TextRenderer = (text) => {
    if (text === null || text === undefined || text === "") return "-";
    return String(text);
  };
  TextRenderer.displayName = `TextRenderer_${key}`;
  return TextRenderer;
};

/**
 * Generates dynamic columns based on data structure, with support for grouped headers.
 * @param {Array} dataSource - Array of data objects
 * @returns {Array} - Array of column configurations
 */
const generateDynamicColumns = (dataSource) => {
  if (!dataSource || dataSource.length === 0) return [];

  const sampleRecord = dataSource[0];
  const topLevelKeys = Object.keys(sampleRecord);
  const columns = [];

  topLevelKeys.forEach((key) => {
    const sampleValue = sampleRecord[key];
    const keyLower = key.toLowerCase();

    // If it's a nested object (and not an array), create grouped columns
    if (
      typeof sampleValue === "object" &&
      sampleValue !== null &&
      !Array.isArray(sampleValue)
    ) {
      const groupTitle = generateTitle(key);
      const subKeys = Object.keys(sampleValue);

      const childColumns = subKeys
        .map((subKey) => {
          const subKeyLower = subKey.toLowerCase();

          // Skip ID fields
          if (subKeyLower.includes("id") && subKeyLower !== "mobile") {
            return null;
          }

          const subSampleValue = sampleValue[subKey];

          // Special handling for the 'address' object - create separate columns for each address field
          if (
            subKey === "address" &&
            typeof subSampleValue === "object" &&
            subSampleValue !== null
          ) {
            // Return null here - we'll handle address columns separately below
            return null;
          }

          return {
            title: generateTitle(subKey),
            dataIndex: [key, subKey],
            key: `${key}_${subKey}`,
            width: getColumnWidth(subKey, subSampleValue),
            render: getCustomRender(subKey, subSampleValue),
            ellipsis: true,
          };
        })
        .filter(Boolean); // Remove nulls from skipped ID fields

      // Handle address object separately - create a grouped column with Address as main header
      if (sampleValue.address && typeof sampleValue.address === "object") {
        
        const addressChildColumns = Object.keys(sampleValue.address).map((addressKey) => {
          const addressSampleValue = sampleValue.address[addressKey];
          
          // Create a custom render function that handles the nested data access
          const AddressFieldRenderer = (text, record) => {
            try {
              const value = record?.[key]?.address?.[addressKey];
              if (value === null || value === undefined || value === "") return "-";
              return String(value);
            } catch (error) {
              console.error(`Error accessing ${key}.address.${addressKey}:`, error);
              return "-";
            }
          };
          AddressFieldRenderer.displayName = `AddressFieldRenderer_${key}_${addressKey}`;
          
          return {
            title: generateTitle(addressKey),
            dataIndex: [key, "address", addressKey],
            key: `${key}_address_${addressKey}`,
            width: getColumnWidth(addressKey, addressSampleValue),
            render: AddressFieldRenderer,
            ellipsis: true,
          };
        });
        
        // Create a grouped column for Address
        const addressGroupColumn = {
          title: "Address",
          children: addressChildColumns,
        };
        
        // Add the Address group column to child columns
        childColumns.push(addressGroupColumn);
      }

      if (childColumns.length > 0) {
        columns.push({
          title: groupTitle,
          children: childColumns,
        });
      }
    } else {
      // Regular column for non-object fields
      const column = {
        title: generateTitle(key),
        dataIndex: key,
        key: key,
        width: getColumnWidth(key, sampleValue),
        render: getCustomRender(key, sampleValue),
        ellipsis: true,
      };

      if (
        keyLower.includes("slno") ||
        keyLower.includes("count") ||
        typeof sampleValue === "number"
      ) {
        column.align = "center";
      }

      columns.push(column);
    }
  });

  return columns;
};

/**
 * Gets appropriate icon for summary metrics
 * @param {string} key - Metric key
 * @returns {JSX.Element} - Icon component
 */
const getMetricIcon = (key) => {
  const keyLower = key.toLowerCase();
  const iconStyle = { fontSize: "24px" };

  if (keyLower.includes("total") && keyLower.includes("drs")) {
    return <FileTextOutlined style={{ ...iconStyle, color: "#1890ff" }} />;
  }
  if (keyLower.includes("total") && keyLower.includes("shipment")) {
    return <DatabaseOutlined style={{ ...iconStyle, color: "#52c41a" }} />;
  }
  if (keyLower.includes("count")) {
    return <AppstoreOutlined style={{ ...iconStyle, color: "#722ed1" }} />;
  }
  if (keyLower.includes("user") || keyLower.includes("agent")) {
    return <UserOutlined style={{ ...iconStyle, color: "#fa8c16" }} />;
  }
  if (keyLower.includes("date") || keyLower.includes("time")) {
    return <CalendarOutlined style={{ ...iconStyle, color: "#fa8c16" }} />;
  }

  return <AppstoreOutlined style={{ ...iconStyle, color: "#666666" }} />;
};

/**
 * Gets appropriate color for summary metrics
 * @param {string} key - Metric key
 * @returns {string} - Color hex code
 */
const getMetricColor = (key) => {
  const keyLower = key.toLowerCase();

  if (keyLower.includes("total") && keyLower.includes("drs")) return "#1890ff";
  if (keyLower.includes("total") && keyLower.includes("shipment"))
    return "#52c41a";
  if (keyLower.includes("count")) return "#722ed1";
  if (keyLower.includes("user") || keyLower.includes("agent")) return "#fa8c16";
  if (keyLower.includes("date") || keyLower.includes("time")) return "#fa8c16";

  return "#666666";
};

/**
 * Generates dynamic summary cards from API response
 * @param {Object} reportSummary - Summary data from API
 * @param {Object} dateRange - Current date range
 * @returns {Array} - Array of card configurations
 */
const generateDynamicSummaryCards = (reportSummary, dateRange) => {
  const cards = [];

  // Process totals from API response
  if (reportSummary && reportSummary.totals) {
    Object.entries(reportSummary.totals).forEach(([key, value]) => {
      cards.push({
        key,
        title: generateTitle(key),
        value: typeof value === "number" ? value : String(value),
        icon: getMetricIcon(key),
        color: getMetricColor(key),
        isNumeric: typeof value === "number",
      });
    });
  }

  // Add additional summary cards from other parts of response if available
  if (reportSummary && reportSummary.metrics) {
    Object.entries(reportSummary.metrics).forEach(([key, value]) => {
      cards.push({
        key: `metric_${key}`,
        title: generateTitle(key),
        value: typeof value === "number" ? value : String(value),
        icon: getMetricIcon(key),
        color: getMetricColor(key),
        isNumeric: typeof value === "number",
      });
    });
  }

  // Add date range card
  // cards.push({
  //   key: "dateRange",
  //   title: "Date Range",
  //   value: `${moment(dateRange.startDate, dateFormat_dmy).format("DD MMM")} - ${moment(dateRange.endDate, dateFormat_dmy).format("DD MMM YYYY")}`,
  //   icon: <CalendarOutlined style={{ fontSize: "24px", color: "#fa8c16" }} />,
  //   color: "#fa8c16",
  //   isDate: true,
  // });

  return cards;
};

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { DRS_UNBOOKED_SHIPMENTS_REPORT, DRS_EXPORT_UNBOOKED_SHIPMENTS_CSV } =
  urls;

const UnbookedShipmentsReport = () => {
  const [tableData, setTableData] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [reportSummary, setReportSummary] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: moment().format(dateFormat_dmy),
    endDate: moment().format(dateFormat_dmy),
  });

  const userinfo = JSON.parse(localStorage.getItem("userinfo"));

  // Fetch report data
  const fetchReportData = async (
    page = 1,
    pageSizeParam = 10,
    customStartDate = null,
    customEndDate = null
  ) => {
    if (loading) {
      console.log("API call already in progress, skipping...");
      return;
    }

    setLoading(true);
    try {
      const startDate = customStartDate || dateRange.startDate;
      const endDate = customEndDate || dateRange.endDate;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pageSizeParam.toString(),
        startDate: moment(startDate, dateFormat_dmy).format("YYYY-MM-DD"),
        endDate: moment(endDate, dateFormat_dmy).format("YYYY-MM-DD"),
      });

      const result = await API.Api(
        `${DRS_UNBOOKED_SHIPMENTS_REPORT}?${queryParams.toString()}`
      ).get();

      if (result?.status && result?.response?.data?.status === 200) {
        const response = result.response.data;

        setTableData(response.data || []);
        setTotal(response.pagination?.totalRecords || 0);
        setReportSummary(response.reportSummary || null);
        setPageNum(page);

        message.success(response.message || "Report data fetched successfully");
      } else {
        setTableData([]);
        setTotal(0);
        setReportSummary(null);
        message.error(
          result?.response?.data?.message || "Failed to fetch report data"
        );
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      message.error(error?.message || "Error fetching report data");
      setTableData([]);
      setTotal(0);
      setReportSummary(null);
    } finally {
      setLoading(false);
    }
  };

  // Export CSV function
  const exportToCSV = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams({
        startDate: moment(dateRange.startDate, dateFormat_dmy).format(
          "YYYY-MM-DD"
        ),
        endDate: moment(dateRange.endDate, dateFormat_dmy).format("YYYY-MM-DD"),
      });

      const result = await API.Api(
        `${DRS_EXPORT_UNBOOKED_SHIPMENTS_CSV}?${queryParams.toString()}`
      ).get();

      if (result?.status && result?.response) {
        // Handle the CSV response - the API returns CSV data directly
        let csvData = result.response;

        // If response is an object with data property, extract it
        if (typeof csvData === "object" && csvData.data) {
          csvData = csvData.data;
        }

        // Ensure csvData is a string
        if (typeof csvData !== "string") {
          csvData = String(csvData);
        }

        // Create blob and download
        const blob = new Blob([csvData], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `unbooked-shipments-report-${moment(
          dateRange.startDate,
          dateFormat_dmy
        ).format("YYYY-MM-DD")}-to-${moment(
          dateRange.endDate,
          dateFormat_dmy
        ).format("YYYY-MM-DD")}.csv`;

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success("Report exported successfully");
      } else {
        message.error(
          result?.response?.data?.message || "Failed to export report"
        );
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      message.error(error?.message || "Error exporting report");
    } finally {
      setExportLoading(false);
    }
  };

  // Handle date range change
  const handleDateChange = (dates) => {
    if (!dates || dates.length === 0) {
      // When dates are cleared - reset to today's date
      const today = moment().format(dateFormat_dmy);
      setDateRange({
        startDate: today,
        endDate: today,
      });
      setPageNum(1);
      fetchReportData(1, pageSize, today, today);
      return;
    }

    if (dates[0] && dates[1]) {
      const newStartDate = moment(dates[0]).format(dateFormat_dmy);
      const newEndDate = moment(dates[1]).format(dateFormat_dmy);

      setDateRange({
        startDate: newStartDate,
        endDate: newEndDate,
      });

      setPageNum(1);
      fetchReportData(1, pageSize, newStartDate, newEndDate);
    }
  };

  // Handle pagination change
  const handlePageChange = (pagination) => {
    setPageSize(pagination.pageSize);
    setPageNum(pagination.current);
    fetchReportData(pagination.current, pagination.pageSize);
  };

  // Generate dynamic columns using utility function
  const columns = generateDynamicColumns(tableData);

  // Pagination config
  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return <a>Previous</a>;
    }
    if (type === "next") {
      return <a>Next</a>;
    }
    return originalElement;
  };

  // Load initial data
  useEffect(() => {
    fetchReportData();
  }, []);

  // Generate dynamic summary cards using utility function
  const summaryCards = generateDynamicSummaryCards(reportSummary, dateRange);

  return (
    <Spin
      spinning={loading}
      indicator={
        <img src={Smcs_Loader} style={{ height: "100px", width: "100px" }} />
      }
    >
      <div className="unbooked-shipments-report-container">
        <Row
          justify="space-between"
          className="header-row"
          style={{ marginBottom: "16px" }}
        >
          <Heading title="Unbooked Shipments Report" />
        </Row>

        {/* Filters Card */}
        <Card title="Select Date Range" className="filters-card">
          <Row gutter={[16, 16]} align="middle">
            <Col xxl={6} xl={6} lg={12} md={14} sm={24} xs={24}>
              <RangePicker
                value={
                  dateRange.startDate && dateRange.endDate
                    ? [
                        moment(dateRange.startDate, dateFormat_dmy),
                        moment(dateRange.endDate, dateFormat_dmy),
                      ]
                    : null
                }
                format={dateFormat_dmy}
                onChange={handleDateChange}
                allowClear={true}
                placeholder={["Start Date", "End Date"]}
                disabledDate={(current) =>
                  current && current > moment().endOf("day")
                }
                className="date-range-picker"
                style={{ width: "100%", height: "40px" }}
                size="large"
              />
            </Col>
            <Col xxl={4} xl={5} lg={6} md={5} sm={12} xs={24}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => fetchReportData(1, pageSize)}
                style={{ width: "100%", height: "40px" }}
              >
                Search
              </Button>
            </Col>
            <Col xxl={4} xl={5} lg={6} md={5} sm={12} xs={24}>
              <Button
                type="primary"
                onClick={exportToCSV}
                loading={exportLoading}
                style={{
                  width: "100%",
                  height: "40px",
                }}
              >
                Export CSV
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Summary Card */}
        {reportSummary && (
          <Card className="summary-card">
            <Row gutter={[16, 16]}>
              {summaryCards.map((card) => (
                <Col
                  xxl={8}
                  xl={8}
                  lg={12}
                  md={12}
                  sm={24}
                  xs={24}
                  key={card.key}
                >
                  <div className="summary-item">
                    <div className="summary-icon">{card.icon}</div>
                    <div className="summary-content">
                      <Text type="secondary">{card.title}</Text>
                      <Title
                        level={card.isDate ? 4 : 3}
                        style={{ margin: 0, color: card.color }}
                      >
                        {card.isDate || !card.isNumeric
                          ? card.value
                          : card.value?.toLocaleString()}
                      </Title>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        )}

        {/* Data Table Card */}
        <Card
          title={`Unbooked Shipments Records`}
          className="data-table-card"
        >
          <TableContainer
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: pageSize,
              total: total,
              itemRender: itemRender,
              current: pageNum,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={handlePageChange}
            rowKey="slNo"
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </Spin>
  );
};

export default UnbookedShipmentsReport;
